import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, IconButton, Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RootState, useAppDispatch, useAppSelector } from '../../../state/state';
import { closeModal } from '../../../state/ModalSlice/ModalSlice';
import { TextFieldComponent, DropdownComponent, RadioComponent, CheckboxComponent, LocationComponent } from '../customFieldsFunctions';
import { doc, updateDoc, arrayRemove, arrayUnion, getDoc } from 'firebase/firestore';
import { db } from '../../../Config/firebase';
import { toast } from 'react-toastify';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';


const EditEventCustomFieldModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((state: RootState) => state.ModalSlice.EditEventCustomFieldModalOpen);
  const serviceProviderId = useAppSelector(selectEffectiveUID); 
  const { question, eventId } = useAppSelector((state: RootState) => state.ArbitrarySlice.ForEditEventCustomFieldModal);
  const [fieldData, setFieldData] = useState<any>({
    fieldDescription: "",
    isRequired: false,
    type: "",
    options: []
  });

  useEffect(() => {
    setFieldData({
      fieldDescription: question.fieldDescription,
      isRequired: question.isRequired,
      type: question.type,
      options: question?.options || []
    });
  }, [question]);

  const handleClose = () => {
    dispatch(closeModal("EditEventCustomFieldModalOpen"));
  };

  const handleFieldChange = (newData: { fieldDescription: string; isRequired: boolean; options?: string[] }) => {
 
    setFieldData({
      fieldDescription: newData.fieldDescription,
      isRequired: newData.isRequired,
      type: question.type,
      options: newData.options || [],
      id: question.id
    });
  };



  const handleSubmit = async (updatedField: { 
    fieldDescription: string; 
    isRequired: boolean; 
    type: string; 
    options?: string[] 
  }) => {
    try {
      const eventRef = doc(db, 'ServiceProviders', serviceProviderId, 'Events', eventId);
      
      // Fetch current document
      const eventDoc = await getDoc(eventRef);
      if (!eventDoc.exists()) {
        throw new Error('Event not found');
      }

      const currentFields = eventDoc.data().additionalFormFields || [];
      const updatedFields = currentFields.map((f: any) => 
        f.id === question.id ? { ...updatedField, id: question.id } : f
      );

      // Update with new array
      await updateDoc(eventRef, {
        additionalFormFields: updatedFields
      });

      toast.success('Field updated successfully');
      handleClose();
    } catch (error) {
      console.error('Error updating custom field:', error);
      toast.error('Error updating field');
    }
  };

  const renderFieldComponent = () => {
    const props = {
      fieldDescription: fieldData.fieldDescription,
      isRequired: fieldData.isRequired,
      existingOptions: fieldData.options,
      onFieldChange: handleFieldChange,
      onSubmit: handleSubmit,
      initialOptions: fieldData.options
    };

    switch (fieldData.type) {
      case 'textfield':
        return <TextFieldComponent {...props} />;
      case 'dropdown':
        return <DropdownComponent {...props} />;
      case 'radio':
        return <RadioComponent {...props} />;
      case 'checkbox':
        return <CheckboxComponent {...props} />;
      case 'location':
        return <LocationComponent {...props} />;
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={isOpen}
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          width: '600px',
          position: 'relative',
          padding: '24px'
        }
      }}
    >
      <DialogContent 
        sx={{ 
          padding: '32px 40px',
          '&.MuiDialogContent-root': {
            padding: '32px 40px',
          }
        }} 
        className="bg-white"
      >
        <IconButton 
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: '16px',
            top: '16px',
            color: 'grey.500',
            '&:hover': {
              color: 'grey.700',
              transform: 'rotate(90deg)',
              transition: 'all 0.3s ease-in-out'
            }
          }}
        >
          <CloseIcon />
        </IconButton>

        <div>
          <div className="mb-8">
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                marginBottom: 1
              }}
            >
              Edit Question
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ color: 'text.secondary' }}
            >
              Update your custom question details
            </Typography>
          </div>

          <Box sx={{ marginTop: 3 }}>
            {renderFieldComponent()}
          </Box>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditEventCustomFieldModal; 